/**
 * Unilar Slider Produtos
 * Initializes FancyApps Carousel on <produtos-slider> elements.
 * On desktop, skips carousel when fewer than 6 slides are present.
 * Distant sliders defer FancyApps until they approach the viewport.
 */

if (!customElements.get('produtos-slider')) {
  class ProdutosSlider extends HTMLElement {
    /** @type {import('fancyapps').Carousel | null} */
    #carousel = null;
    /** @type {MediaQueryList | null} */
    #desktopMq = null;
    /** @type {(() => void) | null} */
    #onBreakpointChange = null;
    /** @type {IntersectionObserver | null} */
    #intersectionObserver = null;
    #hasActivated = false;
    /** @type {number | null} */
    #rafId = null;
    #carouselWaitAttempts = 0;
    /** @type {Set<HTMLElement>} */
    #managedSlides = new Set();
    #onActivationInteraction = () => this.#activate();

    #syncSlideAccessibility = () => {
      if (!this.#carousel || typeof this.#carousel.getVisibleSlides !== 'function') return;

      const visibleSlides = this.#carousel.getVisibleSlides(0);

      this.#carousel.slides.forEach((slide) => {
        const element = slide.el;
        if (!(element instanceof HTMLElement)) return;

        this.#managedSlides.add(element);

        if (visibleSlides.has(slide)) {
          element.removeAttribute('inert');
          element.setAttribute('aria-hidden', 'false');
        } else {
          element.setAttribute('inert', '');
          element.setAttribute('aria-hidden', 'true');
        }
      });
    };

    static DESKTOP_MIN_SLIDES = 6;
    static DESKTOP_MQ = '(min-width: 750px)';
    static CAROUSEL_MAX_WAIT_FRAMES = 60;

    connectedCallback() {
      this.#desktopMq = window.matchMedia(ProdutosSlider.DESKTOP_MQ);
      this.#onBreakpointChange = () => this.#sync();
      this.#desktopMq.addEventListener('change', this.#onBreakpointChange);
      this.#addActivationInteractionListeners();

      if (window.Shopify?.designMode === true || !('IntersectionObserver' in window)) {
        this.#activate();
        return;
      }

      this.#sync();
    }

    disconnectedCallback() {
      this.#disconnectIntersectionObserver();
      this.#cancelCarouselWait();
      this.#removeActivationInteractionListeners();

      if (this.#desktopMq && this.#onBreakpointChange) {
        this.#desktopMq.removeEventListener('change', this.#onBreakpointChange);
      }

      this.#onBreakpointChange = null;
      this.#desktopMq = null;
      this.#destroy();
      this.#hasActivated = false;
    }

    get #slideCount() {
      return this.querySelectorAll('.f-carousel__slide').length;
    }

    get #isDesktopStatic() {
      const isDesktop = this.#desktopMq?.matches ?? window.matchMedia(ProdutosSlider.DESKTOP_MQ).matches;
      return isDesktop && this.#slideCount < ProdutosSlider.DESKTOP_MIN_SLIDES;
    }

    get #defersProductCardInitialization() {
      return this.dataset.deferProductCardInit === 'true';
    }

    #addActivationInteractionListeners() {
      if (!this.#defersProductCardInitialization) return;

      this.addEventListener('focusin', this.#onActivationInteraction);
      this.addEventListener('pointerdown', this.#onActivationInteraction, { passive: true });
    }

    #removeActivationInteractionListeners() {
      this.removeEventListener('focusin', this.#onActivationInteraction);
      this.removeEventListener('pointerdown', this.#onActivationInteraction);
    }

    #activateProductCards() {
      if (!this.#defersProductCardInitialization || this.dataset.productCardsActivated === 'true') return;

      this.dataset.productCardsActivated = 'true';

      this.querySelectorAll('product-card').forEach((card) => {
        if (typeof card.activateDeferredInitialization === 'function') {
          card.activateDeferredInitialization();
        }
      });
    }

    #disconnectIntersectionObserver() {
      this.#intersectionObserver?.disconnect();
      this.#intersectionObserver = null;
    }

    #cancelCarouselWait() {
      if (this.#rafId !== null) {
        cancelAnimationFrame(this.#rafId);
      }

      this.#rafId = null;
      this.#carouselWaitAttempts = 0;
    }

    #destroy() {
      if (this.#carousel) {
        this.#carousel.off('ready change settle refresh', this.#syncSlideAccessibility);
        this.#clearSlideAccessibility();
        this.#carousel.destroy();
      }

      this.#carousel = null;
      this.carousel = null;
      this.classList.remove('is-carousel-active');
    }

    #clearSlideAccessibility() {
      this.#managedSlides.forEach((slide) => {
        slide.removeAttribute('inert');
        slide.removeAttribute('aria-hidden');
      });
      this.#managedSlides.clear();
    }

    #setStatic(isStatic) {
      this.classList.toggle('is-static', isStatic);
    }

    #observeForActivation() {
      if (this.#intersectionObserver || this.#hasActivated) return;

      if (!('IntersectionObserver' in window)) {
        this.#activate();
        return;
      }

      this.#intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.#activate();
          }
        },
        {
          rootMargin: '300px 0px',
          threshold: 0,
        }
      );

      this.#intersectionObserver.observe(this);
    }

    #activate() {
      if (this.#hasActivated) return;

      this.#hasActivated = true;
      this.#activateProductCards();
      this.#removeActivationInteractionListeners();
      this.#disconnectIntersectionObserver();
      this.#sync();
    }

    #sync() {
      if (this.#isDesktopStatic) {
        this.#cancelCarouselWait();
        this.#destroy();
        this.#setStatic(true);

        if (this.#hasActivated || !this.#defersProductCardInitialization) {
          this.#disconnectIntersectionObserver();
        } else {
          this.#observeForActivation();
        }

        return;
      }

      this.#setStatic(false);

      if (this.#hasActivated) {
        this.#initCarousel();
        return;
      }

      this.#observeForActivation();
    }

    #initCarousel() {
      if (!this.#hasActivated || this.#carousel || this.#rafId !== null) return;

      const start = () => {
        this.#rafId = null;

        if (!this.isConnected || !this.#hasActivated || this.#carousel) return;

        if (this.#isDesktopStatic) {
          this.#setStatic(true);
          return;
        }

        if (!window.Carousel) {
          this.#carouselWaitAttempts += 1;

          if (this.#carouselWaitAttempts >= ProdutosSlider.CAROUSEL_MAX_WAIT_FRAMES) {
            this.#carouselWaitAttempts = 0;
            return;
          }

          this.#rafId = requestAnimationFrame(start);
          return;
        }

        this.#carouselWaitAttempts = 0;

        const carousel = new window.Carousel(this, {
          Dots: this.dataset.dots !== 'false',
          Navigation: this.dataset.navigation === 'true',
          infinite: true,
          center: false,
          fill: false,
          slidesPerPage: 1,
        });

        this.#carousel = carousel;
        this.carousel = carousel;

        carousel.on('ready', () => {
          if (this.#carousel === carousel) {
            this.classList.add('is-carousel-active');
          }
        });

        carousel.on('ready change settle refresh', this.#syncSlideAccessibility);
      };

      start();
    }
  }

  customElements.define('produtos-slider', ProdutosSlider);
}
